import React, { useState } from 'react'
import { apiPut } from '../../utils/api'

interface AgentAvailabilityToggleProps {
  currentStatus: string
  onStatusChange: (status: string) => void
}

const STATUSES = [
  { value: 'available', label: 'Available', color: 'bg-green-500', textColor: 'text-green-700 dark:text-green-400' },
  { value: 'busy', label: 'Busy', color: 'bg-amber-500', textColor: 'text-amber-700 dark:text-amber-400' },
  { value: 'offline', label: 'Offline', color: 'bg-gray-400', textColor: 'text-gray-500 dark:text-gray-400' },
]

export const AgentAvailabilityToggle: React.FC<AgentAvailabilityToggleProps> = ({ currentStatus, onStatusChange }) => {
  const [open, setOpen] = useState(false)
  const [updating, setUpdating] = useState(false)
  const current = STATUSES.find(s => s.value === currentStatus) || STATUSES[2]

  const handleChange = async (status: string) => {
    setUpdating(true)
    try {
      await apiPut('/agents/availability', { status })
      onStatusChange(status)
    } catch (err) {
      console.error('Failed to update availability:', err)
    } finally {
      setUpdating(false)
      setOpen(false)
    }
  }

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        disabled={updating}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border border-border hover:bg-surface-hover transition-colors ${current.textColor}`}
      >
        <span className={`w-2 h-2 rounded-full ${current.color} ${currentStatus === 'available' ? 'animate-pulse' : ''}`} />
        {current.label}
        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-40 rounded-lg border border-border bg-surface-elevated shadow-float z-50 py-1">
          {STATUSES.map(s => (
            <button
              key={s.value}
              onClick={() => handleChange(s.value)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-surface-hover transition-colors ${
                s.value === currentStatus ? 'font-semibold' : ''
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${s.color}`} />
              <span className={s.textColor}>{s.label}</span>
              {s.value === currentStatus && (
                <svg className="w-3.5 h-3.5 ml-auto text-brand-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
